/**
 * ViewSwitcher — the saved-views sidebar for a list surface.
 *
 * WHY: the auditor flagged that saved views had persistence but zero
 * UI — no create/name/pin/share, no sections, no table↔kanban switch.
 * This is that UI: a sidebar listing Pinned / My views / Team sections
 * (split by the tested pure `partitionViews`), an inline create-and-name
 * form, pin + share toggles per view, a table↔kanban lens switch for
 * the active view, and delete. It renders state and emits intents —
 * persistence stays with the caller (src/lib/db/savedViews.ts).
 */

import { useState } from 'react';
import {
  Kanban,
  Lock,
  Pin,
  PinOff,
  Plus,
  Table2,
  Trash2,
  Users,
} from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import { partitionViews, type SavedView, type ViewType } from '../lib/crm/viewConfig';

interface ViewSwitcherProps {
  /** All rich saved views for this object type (owner-filtered by partition). */
  views: SavedView[];
  /** The identity view shown above the sections ("All accounts"). */
  defaultViewItem: SavedView;
  activeId: string;
  userId: string;
  /** False for viewer role — hides all mutating controls. */
  canMutate: boolean;
  onSelect: (view: SavedView) => void;
  onCreate: (name: string) => void;
  onTogglePin: (view: SavedView) => void;
  onToggleVisibility: (view: SavedView) => void;
  onSwitchType: (view: SavedView, t: ViewType) => void;
  onDelete: (view: SavedView) => void;
}

function ViewRow({
  view,
  active,
  canMutate,
  onSelect,
  onTogglePin,
  onToggleVisibility,
  onDelete,
}: {
  view: SavedView;
  active: boolean;
  canMutate: boolean;
  onSelect: () => void;
  onTogglePin?: () => void;
  onToggleVisibility?: () => void;
  onDelete?: () => void;
}) {
  const TypeIcon = view.viewType === 'kanban' ? Kanban : Table2;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        borderRadius: 8,
        padding: '2px 4px',
        background: active
          ? 'linear-gradient(135deg, rgba(255,0,255,0.16), rgba(187,65,152,0.10))'
          : 'transparent',
      }}
    >
      <button
        type="button"
        className="glass-btn"
        aria-current={active ? 'true' : undefined}
        onClick={onSelect}
        style={{
          flex: 1,
          minWidth: 0,
          justifyContent: 'flex-start',
          border: 'none',
          background: 'transparent',
          padding: '5px 6px',
          color: active ? '#fff' : C.textMuted,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
        title={`${view.name} · ${view.viewType}`}
      >
        <TypeIcon size={12} style={{ flexShrink: 0 }} />
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 12,
            fontWeight: active ? 700 : 500,
          }}
        >
          {view.name}
        </span>
      </button>
      {canMutate && onTogglePin && (
        <button
          type="button"
          className="glass-btn"
          aria-label={view.pinned ? `Unpin ${view.name}` : `Pin ${view.name}`}
          title={view.pinned ? 'Unpin' : 'Pin'}
          onClick={onTogglePin}
          style={{ padding: '3px 5px', border: 'none', background: 'transparent' }}
        >
          {view.pinned ? (
            <PinOff size={11} color={C.magenta} />
          ) : (
            <Pin size={11} color={C.textMuted} />
          )}
        </button>
      )}
      {canMutate && onToggleVisibility && (
        <button
          type="button"
          className="glass-btn"
          aria-label={
            view.visibility === 'team'
              ? `Make ${view.name} personal`
              : `Share ${view.name} with team`
          }
          title={view.visibility === 'team' ? 'Shared with team — make personal' : 'Personal — share with team'}
          onClick={onToggleVisibility}
          style={{ padding: '3px 5px', border: 'none', background: 'transparent' }}
        >
          {view.visibility === 'team' ? (
            <Users size={11} color={C.green} />
          ) : (
            <Lock size={11} color={C.textMuted} />
          )}
        </button>
      )}
      {canMutate && onDelete && (
        <button
          type="button"
          className="glass-btn"
          aria-label={`Delete ${view.name}`}
          title="Delete view"
          onClick={onDelete}
          style={{ padding: '3px 5px', border: 'none', background: 'transparent' }}
        >
          <Trash2 size={11} color={C.textMuted} />
        </button>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 2 }}>
      <span className="t-section-title" style={{ fontSize: 10, padding: '0 4px' }}>
        {title}
      </span>
      {children}
    </div>
  );
}

export function ViewSwitcher({
  views,
  defaultViewItem,
  activeId,
  userId,
  canMutate,
  onSelect,
  onCreate,
  onTogglePin,
  onToggleVisibility,
  onSwitchType,
  onDelete,
}: ViewSwitcherProps) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');

  const { pinned, personal, team } = partitionViews(views, userId);
  const active =
    views.find((v) => v.id === activeId) ??
    (defaultViewItem.id === activeId ? defaultViewItem : defaultViewItem);

  const submitCreate = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onCreate(trimmed);
    setName('');
    setCreating(false);
  };

  return (
    <GlassPanel variant="subtle" animate={false} style={{ padding: 10, display: 'grid', gap: 10, alignSelf: 'start' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span className="t-section-title">Views</span>
        <div style={{ flex: 1 }} />
        {canMutate && (
          <button
            type="button"
            className="glass-btn"
            aria-label="New view"
            title="Save the current lens as a named view"
            onClick={() => setCreating((c) => !c)}
            style={{ padding: '3px 7px' }}
          >
            <Plus size={12} />
          </button>
        )}
      </div>

      {creating && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submitCreate();
          }}
          style={{ display: 'flex', gap: 4 }}
        >
          <input
            className="glass-input"
            autoFocus
            placeholder="View name…"
            aria-label="New view name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ flex: 1, minWidth: 0, padding: '5px 8px', fontSize: 12 }}
          />
          <button
            type="submit"
            className="glass-btn glass-btn--primary"
            disabled={!name.trim()}
            style={{ padding: '4px 10px', fontSize: 12 }}
          >
            Save
          </button>
        </form>
      )}

      {/* Identity view — always present, never persisted. */}
      <ViewRow
        view={defaultViewItem}
        active={activeId === defaultViewItem.id}
        canMutate={false}
        onSelect={() => onSelect(defaultViewItem)}
      />

      {pinned.length > 0 && (
        <Section title="Pinned">
          {pinned.map((v) => (
            <ViewRow
              key={v.id}
              view={v}
              active={v.id === activeId}
              canMutate={canMutate}
              onSelect={() => onSelect(v)}
              onTogglePin={() => onTogglePin(v)}
              onToggleVisibility={() => onToggleVisibility(v)}
              onDelete={() => onDelete(v)}
            />
          ))}
        </Section>
      )}

      <Section title="My views">
        {personal.length === 0 ? (
          <span className="t-meta" style={{ padding: '2px 4px' }}>
            {canMutate ? 'None yet — press + to save one.' : 'None yet.'}
          </span>
        ) : (
          personal.map((v) => (
            <ViewRow
              key={v.id}
              view={v}
              active={v.id === activeId}
              canMutate={canMutate}
              onSelect={() => onSelect(v)}
              onTogglePin={() => onTogglePin(v)}
              onToggleVisibility={() => onToggleVisibility(v)}
              onDelete={() => onDelete(v)}
            />
          ))
        )}
      </Section>

      {team.length > 0 && (
        <Section title="Team">
          {team.map((v) => (
            <ViewRow
              key={v.id}
              view={v}
              active={v.id === activeId}
              canMutate={canMutate}
              onSelect={() => onSelect(v)}
              onTogglePin={() => onTogglePin(v)}
              onToggleVisibility={() => onToggleVisibility(v)}
              onDelete={() => onDelete(v)}
            />
          ))}
        </Section>
      )}

      {/* Lens switch for the active view: same data, table or kanban. */}
      <div style={{ display: 'grid', gap: 4, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 8 }}>
        <span className="t-section-title" style={{ fontSize: 10 }}>
          Lens
        </span>
        <div
          role="tablist"
          aria-label="View lens"
          style={{ display: 'inline-flex', gap: 2, padding: 2, borderRadius: 10, border: '1px solid rgba(255,255,255,0.10)' }}
        >
          {(
            [
              { t: 'table' as const, label: 'Table', Icon: Table2 },
              { t: 'kanban' as const, label: 'Kanban', Icon: Kanban },
            ] as const
          ).map(({ t, label, Icon }) => {
            const selected = active.viewType === t;
            return (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={selected}
                className="glass-btn"
                onClick={() => onSwitchType(active, t)}
                style={{
                  flex: 1,
                  padding: '5px 10px',
                  border: 'none',
                  fontSize: 12,
                  background: selected
                    ? 'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(187,65,152,0.14))'
                    : 'transparent',
                  color: selected ? '#fff' : C.textMuted,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <Icon size={12} />
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </GlassPanel>
  );
}
